import React from "react";
import { Card, Row, Col, Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faUser,
  faIdCard,
  faPhone,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useGetCustomerByIdQuery } from "../../../../redux/services/customerService";

interface CustomerDetailViewProps {
  customerId?: string;
}

const CustomerDetailView: React.FC<CustomerDetailViewProps> = ({
  customerId,
}) => {
  const { id } = useParams<{ id: string }>();

  const custId = customerId || id;

  const {
    data: customerDetails,
    isLoading,
    error,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refetch,
  } = useGetCustomerByIdQuery(custId!, {
    skip: !custId,
  });

  // Handle error state
  React.useEffect(() => {
    if (error) {
      console.error("Error loading customer:", error);
      toast.error("Error loading customer data");
    }
  }, [error]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatDate = (timestamp: {
    _seconds: number;
    _nanoseconds: number;
  }) => {
    if (!timestamp || !timestamp._seconds) return "N/A";
    return new Date(timestamp._seconds * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <span className="ms-2">Loading customer details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="d-flex justify-content-center align-items-center flex-column"
        style={{ height: "50vh" }}
      >
        <div className="alert alert-danger mb-3">
          Error loading customer data. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="customer-detail-view">
      {/* Customer Details Card */}
      <Card className="shadow-sm">
        <Card.Body>
          <Row className="mb-4">
            <Col md={8}>
              <h4 className="text-primary">
                {customerDetails?.displayName || "Unnamed Customer"}
              </h4>
              <p className="text-muted mb-0">
                Customer ID: {customerDetails?.id}
              </p>
              {customerDetails?.userId && (
                <p className="text-muted mb-0">
                  User ID: {customerDetails.userId}
                </p>
              )}
            </Col>
          </Row>

          <hr />

          {/* Personal Information */}
          <h6 className="section-title mb-3 text-primary">
            <FontAwesomeIcon icon={faIdCard} className="me-2" />
            Personal Information
          </h6>
          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <strong>
                <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                Display Name:
              </strong>
              <div className="text-muted mt-1">
                {customerDetails?.displayName || "Not provided"}
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <strong>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="me-2 text-muted"
                />
                Email:
              </strong>
              <div className="text-muted mt-1">
                {customerDetails?.email || "Not provided"}
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <strong>
                <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                Phone Number:
              </strong>
              <div className="text-muted mt-1">
                {customerDetails?.phoneNumber || "Not provided"}
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <strong>User Type:</strong>
              <div className="mt-1">
                <Badge
                  bg={customerDetails?.isAnonymousUser ? "warning" : "info"}
                >
                  {customerDetails?.isAnonymousUser
                    ? "Anonymous"
                    : "Registered"}
                </Badge>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CustomerDetailView;
